import { type ComponentProps } from "react";
import { type EnvironmentId, type ThreadId, type TurnId } from "@cmmd-forge/contracts";
import { type ThreadTerminalState } from "../../terminalStateStore";
import { type Project, type Thread } from "../../types";
import type { ComposerDockInput } from "./ChatComposerDock";
import { buildHeaderProps } from "./buildThreadHeaderProps";
import {
  type ChatLoadedThreadSurfaceProps,
  ChatLoadedThreadSurface,
} from "./ChatLoadedThreadSurface";
import { type ExpandedImagePreview } from "./ExpandedImagePreview";
import { type TerminalLaunchContext } from "./PersistentThreadTerminalDrawer";
import { type ChatComposerPrepRows } from "./ChatComposerPrepRows";
import { type ChatTimelinePanel } from "./ChatTimelinePanel";
import { type ChatComposerDock } from "./ChatComposerDock";
import { type ChatThreadOverlays } from "./ChatThreadOverlays";

type HeaderProps = ChatLoadedThreadSurfaceProps["headerProps"];
type TimelinePanelProps = ComponentProps<typeof ChatTimelinePanel>;
type PrepRowsProps = ComponentProps<typeof ChatComposerPrepRows>;
type ComposerDockProps = ComponentProps<typeof ChatComposerDock>;
type OverlayProps = ComponentProps<typeof ChatThreadOverlays>;
type ComposerProps = ComposerDockInput;

export function buildChatLoadedThreadSurfaceProps(
  input: LoadedSurfaceInput,
): ChatLoadedThreadSurfaceProps {
  return {
    anchorKind: input.activeThread.anchorKind ?? "scratch",
    anchorCmmdId: input.activeThread.anchorCmmdId ?? null,
    providerStatus: input.activeProviderStatus,
    rateLimitBannerProps: {
      environmentId: input.environmentId,
      projectId: input.activeProject?.cmmdRegistration?.cmmdProjectId ?? null,
      threadId: input.activeThread.id,
      modelSelection: input.activeThread.modelSelection,
      rateLimitStatus: input.activeThread.rateLimitStatus,
      threadError: input.activeThread.error,
      runtimeMode: input.activeThread.runtimeMode,
      interactionMode: input.activeThread.interactionMode,
      onResumed: () => input.setThreadError(input.activeThread.id, null),
    },
    threadError: input.activeThread.error,
    onDismissThreadError: () => input.setThreadError(input.activeThread.id, null),
    headerProps: buildHeaderProps(input),
    timelinePanelProps: buildTimelinePanelProps(input),
    prepRowsProps: buildPrepRowsProps(input),
    composerDockProps: buildComposerDockProps(input),
    overlayProps: buildOverlayProps(input),
  } satisfies ComponentProps<typeof ChatLoadedThreadSurface>;
}

function buildTimelinePanelProps(input: LoadedSurfaceInput): TimelinePanelProps {
  return {
    timelineKey: input.activeThread.id,
    timelineProps: {
      ...input.timelineProps,
      activeThreadEnvironmentId: input.activeThread.environmentId,
      markdownCwd: input.gitCwd ?? undefined,
      // Same source the composer uses for its `$skill` autocomplete, so a skill
      // that can be typed is a skill that renders back as a chip.
      providerSkills: input.activeProviderStatus?.skills ?? [],
      workspaceRoot: input.activeWorkspaceRoot,
      emptyStateProjectName: input.activeProject?.name ?? null,
    },
    showScrollToBottom: input.showScrollToBottom,
    onScrollToBottom: () => input.scrollToEnd(true),
  };
}

function buildPrepRowsProps(input: LoadedSurfaceInput): PrepRowsProps {
  return {
    activeThreadKind: input.activeThread.kind,
    parentThreadId: input.activeThread.parentThreadId,
    environmentId: input.environmentId,
    pendingAutoCreatePrompt: input.pendingAutoCreatePrompt,
    onApprovePlan: input.onApprovePlan,
    onEditPlan: input.onEditPlan,
    onDiscardPlan: input.onDiscardPlan,
    showAgentsRow: input.showAgentsRow,
    showWorkBar: Boolean(input.activeProject && input.activeWorkspaceRoot),
    activeThreadTeam: input.activeThreadTeam,
    onOpenWorkerThread: input.onOpenWorkerThread,
    activeTaskContextModel: input.activeTaskContextModel,
    onViewActiveTaskContext: input.onViewActiveTaskContext,
    serverThreadStatus: input.serverThreadStatus,
    serverThreadSubscriptionError: input.serverThreadSubscriptionError,
    activePlan: input.activePlan,
  };
}

/** Passes composer state, notices, and queue actions to the dock. */
function buildComposerDockProps(input: LoadedSurfaceInput): ComposerDockProps {
  return {
    approval: buildApprovalProps(input),
    composerProps: buildComposerProps(input),
    usageNotice: input.usageNotice,
    queuedMessages: input.queuedMessages,
    onSteerQueuedMessage: input.onSteerQueuedMessage,
    onRemoveQueuedMessage: input.onRemoveQueuedMessage,
  };
}

function buildOverlayProps(input: LoadedSurfaceInput): OverlayProps {
  return {
    mountedTerminalThreadRefs: input.mountedTerminalThreadRefs,
    activeThreadKey: input.activeThreadKey,
    terminalOpen: Boolean(input.terminalState.terminalOpen),
    activeTerminalLaunchContext: input.activeTerminalLaunchContext,
    terminalFocusRequestId: input.terminalFocusRequestId,
    splitTerminalShortcutLabel: input.splitTerminalShortcutLabel,
    newTerminalShortcutLabel: input.newTerminalShortcutLabel,
    closeTerminalShortcutLabel: input.closeTerminalShortcutLabel,
    onAddTerminalContext: input.addTerminalContextToDraft,
    expandedImage: input.expandedImage,
    onCloseExpandedImage: input.closeExpandedImage,
  };
}

function buildApprovalProps(input: LoadedSurfaceInput): ComposerDockProps["approval"] {
  if (!input.isGitHubApprovalOverlayVisible || !input.activePendingGitHubConfirmation) {
    return null;
  }
  return {
    confirmation: input.activePendingGitHubConfirmation,
    onApprove: input.onApproveGitHubConfirmation,
    onDecline: input.onDeclineGitHubConfirmation,
    onSendFeedback: input.onGitHubApprovalFeedback,
    onDismiss: () =>
      input.setGitHubApprovalOverlayDismissed(
        input.activePendingGitHubConfirmation!.confirmationId,
      ),
  };
}

function buildComposerProps(input: LoadedSurfaceInput): ComposerProps {
  return {
    ...input.composerProps,
    activeThreadEnvironmentId: input.activeThread.environmentId,
    activeThread: input.activeThread,
    activeThreadActivities: input.activeThread.activities,
    activeProjectTitle: input.activeProject?.name ?? null,
    activeThreadWorktreePath: input.activeThreadWorktreePath,
    activeThreadBranch: input.activeThreadBranch ?? input.gitBranch,
    activeThreadEnvMode: input.activeThreadEnvMode,
    pullRequest: {
      number: input.gitPrNumber,
      state: input.gitPrState,
      url: input.gitPrUrl,
      title: input.gitPrTitle,
    },
  };
}

export interface LoadedSurfaceInput {
  readonly onRunScript?: HeaderProps["onRunScript"];
  readonly activeThread: Thread;
  readonly activeProject: Project | null | undefined;
  readonly activeProviderStatus: ChatLoadedThreadSurfaceProps["providerStatus"];
  readonly usageNotice: ComposerDockProps["usageNotice"];
  /** Queued follow-up messages for the active thread, oldest first. */
  readonly queuedMessages?: ComposerDockProps["queuedMessages"];
  readonly onSteerQueuedMessage?: ComposerDockProps["onSteerQueuedMessage"];
  readonly onRemoveQueuedMessage?: ComposerDockProps["onRemoveQueuedMessage"];
  readonly setThreadError: (threadId: ThreadId | null, error: string | null) => void;
  readonly activeProjectCwd: string | null;
  readonly activeThreadWorktreePath: string | null;
  readonly activeThreadBranch: string | null;
  readonly activeThreadEnvMode: ComposerProps["activeThreadEnvMode"];
  readonly gitBranch: string | null;
  readonly gitPrState: ComposerProps["pullRequest"]["state"];
  readonly gitPrNumber: number | null;
  readonly gitPrUrl: string | null;
  readonly gitPrTitle: string | null;
  readonly activeThreadParentTitle: string | null;
  readonly activeThreadWorkerCount: number;
  readonly jumpToParentShortcutLabel: string | null;
  readonly onJumpToParent: () => void;
  readonly isServerThread: boolean;
  readonly canOpenRightSidebar: boolean;
  readonly previewOpen: boolean;
  readonly canvasOpen: boolean;
  readonly diffOpen: boolean;
  readonly planOpen: boolean;
  readonly terminalState: ThreadTerminalState;
  readonly onTogglePreview: () => void;
  readonly onToggleCanvasInvert: () => void;
  readonly onToggleDiff: () => void;
  readonly togglePlanSidebar: () => void;
  readonly toggleTerminalVisibility: () => void;
  readonly previewPanelShortcutLabel: string | null;
  readonly canvasPanelShortcutLabel: string | null;
  readonly diffPanelShortcutLabel: string | null;
  readonly planPanelShortcutLabel: string | null;
  readonly terminalToggleShortcutLabel: string | null;
  readonly showRightPanelLauncher: boolean;
  readonly gitCwd: string | null;
  readonly activeThreadRef: HeaderProps["activeThreadRef"];
  readonly routeKind: "server" | "draft";
  readonly draftId: HeaderProps["draftId"] | null;
  readonly timelineProps: Omit<
    TimelinePanelProps["timelineProps"],
    | "activeThreadEnvironmentId"
    | "markdownCwd"
    | "providerSkills"
    | "workspaceRoot"
    | "emptyStateProjectName"
  >;
  readonly showScrollToBottom: boolean;
  readonly scrollToEnd: (force: boolean) => void;
  readonly environmentId: EnvironmentId;
  readonly pendingAutoCreatePrompt: PrepRowsProps["pendingAutoCreatePrompt"];
  readonly onApprovePlan: PrepRowsProps["onApprovePlan"];
  readonly onEditPlan: PrepRowsProps["onEditPlan"];
  readonly onDiscardPlan: PrepRowsProps["onDiscardPlan"];
  readonly showAgentsRow: boolean;
  readonly activeWorkspaceRoot: string | undefined;
  readonly activeThreadTeam: PrepRowsProps["activeThreadTeam"];
  readonly onOpenWorkerThread: (targetThreadId: ThreadId) => void;
  readonly activeTaskContextModel: PrepRowsProps["activeTaskContextModel"];
  readonly onViewActiveTaskContext: () => void;
  readonly serverThreadStatus: PrepRowsProps["serverThreadStatus"];
  readonly serverThreadSubscriptionError: string | null | undefined;
  readonly activePlan: { readonly turnId?: TurnId } | null;
  readonly isGitHubApprovalOverlayVisible: boolean;
  readonly activePendingGitHubConfirmation: ComposerDockProps["approval"] extends infer Approval
    ? Approval extends { readonly confirmation: infer Confirmation }
      ? Confirmation | null
      : never
    : never;
  readonly onApproveGitHubConfirmation: NonNullable<ComposerDockProps["approval"]>["onApprove"];
  readonly onDeclineGitHubConfirmation: NonNullable<ComposerDockProps["approval"]>["onDecline"];
  readonly onGitHubApprovalFeedback: NonNullable<ComposerDockProps["approval"]>["onSendFeedback"];
  readonly setGitHubApprovalOverlayDismissed: (confirmationId: string) => void;
  readonly composerProps: Omit<
    ComposerProps,
    | "activeThreadEnvironmentId"
    | "activeThread"
    | "activeThreadActivities"
    // Run context is derived here, where the project and the thread's checkout
    // are already in hand, rather than threaded through every caller.
    | "activeProjectTitle"
    | "activeThreadWorktreePath"
    | "activeThreadBranch"
    | "activeThreadEnvMode"
    | "pullRequest"
  >;
  readonly mountedTerminalThreadRefs: OverlayProps["mountedTerminalThreadRefs"];
  readonly activeThreadKey: string | null;
  readonly activeTerminalLaunchContext: TerminalLaunchContext | null;
  readonly terminalFocusRequestId: number;
  readonly splitTerminalShortcutLabel: string | null;
  readonly newTerminalShortcutLabel: string | null;
  readonly closeTerminalShortcutLabel: string | null;
  readonly addTerminalContextToDraft: OverlayProps["onAddTerminalContext"];
  readonly expandedImage: ExpandedImagePreview | null;
  readonly closeExpandedImage: () => void;
}
